import { useCallback, useMemo, memo, useEffect } from 'react'
import Select, { ValueTransformer } from 'components/Input/Select'
import { Choice } from 'components/Input/ChoiceGroup'
import { useStores } from 'stores/utils/hooks/useStores'
import { observer } from 'mobx-react'
import ConditionalWrapper from 'components/ConditionalWrapper'
import { MAX_SAFE_INTEGER } from 'utils/number'
import SimpleSelect from 'components/Input/SimpleSelect'
import getInitialFormValues from 'formInitialValues'
import FieldDataCollector from '../FieldDataCollector'
import { PathFormFieldProps } from '../../interfaces/PathFormComponents.interface'
import WithFieldInteraction from '../../utils/tracking/amplitude/WithFieldInteraction'
import { useMeta } from '../../stores/utils/hooks/useMeta'
import { usePatchedField } from '../../stores/utils/hooks/usePatchedField'
import { loanSelections } from '../../texts/smava/loan_selector'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'

export const SEPARATOR_VALUE = 'SEPARATOR'

interface OwnProps<T> {
  defaultValue?: T
  choices?: Choice<T>[]
  clue?: string
  dependson?: string
  hasMostSelected?: boolean
  useSimpleSelect?: boolean
  handleLoanSelectorChanges?: boolean
  isSelectLabel?: boolean
  isTooltipHidden?: boolean
  disabled?: boolean
  customLabel?: string
  value?: string | number
  trackValidationErrors?: boolean
}

type Props<T> = PathFormFieldProps<T> & OwnProps<T>

const SelectInput = observer(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <T extends any>({
    path,
    choices: propOptions,
    clue,
    onTooltip,
    onChange,
    defaultValue,
    hasMostSelected,
    useSimpleSelect = false,
    handleLoanSelectorChanges = false,
    dependson,
    outerLabel,
    showTooltipOnFieldClick,
    smallTextField,
    isTooltipHidden,
    isSelectLabel,
    disabled,
    customLabel,
    patch = true,
    value: propValue,
    trackValidationErrors
  }: Props<T>) => {
    const { input, error, meta } = usePatchedField(
      path,
      {
        defaultValue
      },
      patch
    )

    const {
      options = [],
      placeholder,
      tooltip,
      label = '',
      selectLabel
    } = useMeta<T>(path)
    let selectOptions = propOptions || options

    const {
      traversal: {
        removeErrorsForPath,
        handlePathFormFieldChange,
        handleMultiplePathFormFieldChanges,
        data: {
          common: { amount }
        }
      }
    } = useStores()

    if (selectOptions.length && hasMostSelected) {
      const mostSelectedOptions = selectOptions.filter(
        (option) => option.mostSelected
      )
      const otherOptions = selectOptions.filter(
        (option) => !option.mostSelected
      )
      mostSelectedOptions.push({ label: '------', value: SEPARATOR_VALUE as T })
      selectOptions = mostSelectedOptions.concat(otherOptions)
    }

    const changeHandler = useCallback(
      (value: T | undefined) => {
        if (handleLoanSelectorChanges && path === 'common.purpose') {
          if (value === 'REAL_ESTATE_FINANCING') {
            handlePathFormFieldChange('common.amount', 300000)
          } else if (value === 'OTHER') {
            handlePathFormFieldChange(
              'common.amount',
              getInitialFormValues().common.amount
            )
          } else if (value === 'PRE_OWNED_CAR') {
            handleMultiplePathFormFieldChanges(
              [
                {
                  path: 'common.amount',
                  value: getInitialFormValues().common.amount
                },
                {
                  path: 'common.vehicle.price',
                  value: getInitialFormValues().common.amount
                }
              ],
              false
            )
            handlePathFormFieldChange('common.vehicle.initialPayment', 0, false)
          } else {
            const selectionForCategories = loanSelections.filter((selection) =>
              selection.categories.includes(value as string)
            )
            const maxValueForNewPurpose =
              selectionForCategories.length > 0
                ? selectionForCategories.reduce((prev, curr) =>
                    curr.max > prev.max ? curr : prev
                  )?.max
                : MAX_SAFE_INTEGER
            if (amount > maxValueForNewPurpose) {
              handlePathFormFieldChange('common.amount', maxValueForNewPurpose)
            }
          }
        }

        input.onChange(value)
        input.onBlur()

        if (onChange) {
          onChange(value)
        }

        removeErrorsForPath(path)
      },
      [
        input,
        onChange,
        removeErrorsForPath,
        path,
        amount,
        handlePathFormFieldChange,
        handleMultiplePathFormFieldChanges,
        handleLoanSelectorChanges
      ]
    )

    const blurHandler = useCallback(() => {
      input.onBlur()
    }, [input])
    const focusHandler = useCallback(() => {
      input.onFocus()
    }, [input])

    const valueTransformer: ValueTransformer<Choice<T>, T> = useMemo(
      () => ({
        selector: 'value',
        extractor: (choice) => choice.value
      }),
      []
    )

    const getLabel = () => {
      if (customLabel || customLabel === '') {
        return customLabel
      }

      if (isSelectLabel && selectLabel) {
        return selectLabel
      }

      return label
    }

    useEffect(() => {
      if (meta.touched && error && path && trackValidationErrors) {
        trackInAmplitude(AmplitudeEvent.ValidationErrorOccured, {
          error,
          path
        })
      }
    }, [error, meta.touched, path, trackValidationErrors])

    if (useSimpleSelect) {
      return (
        <WithFieldInteraction
          trackFocusEvents={false}
          trackOnChange
          path={path}
        >
          <SimpleSelect
            path={path}
            label={getLabel()}
            options={selectOptions}
            onChange={changeHandler}
            onBlur={blurHandler}
            onFocus={focusHandler}
            name={input.name}
            valueTransformer={valueTransformer}
            placeholder={placeholder}
            value={`${propValue ?? input.value}`}
            error={meta.touched ? error : ''}
            disabled={disabled}
            smallTextField
            outerLabel
            optionLabel="label"
          />
        </WithFieldInteraction>
      )
    }

    return (
      <WithFieldInteraction trackFocusEvents={false} trackOnChange path={path}>
        <Select<Choice<T>, T>
          path={path}
          label={getLabel()}
          options={selectOptions}
          onChange={changeHandler}
          onBlur={blurHandler}
          onFocus={focusHandler}
          name={input.name}
          valueTransformer={valueTransformer}
          placeholder={placeholder}
          value={`${propValue ?? input.value}`}
          error={meta.touched ? error : ''}
          disabled={disabled}
          onTooltip={onTooltip}
          smallTextField={smallTextField}
          showTooltipOnFieldClick={showTooltipOnFieldClick}
          outerLabel={outerLabel}
          dependson={dependson}
          tooltip={!isTooltipHidden ? tooltip : ''}
          clue={clue}
          optionLabel="label"
        />
      </WithFieldInteraction>
    )
  }
)

function PathSelect<T>(props: Props<T>) {
  const {
    path,
    customLabel,
    labelPath,
    dependson,
    choices,
    clue,
    onTooltip,
    onChange,
    defaultValue,
    hasMostSelected,
    useSimpleSelect,
    handleLoanSelectorChanges,
    outerLabel,
    showTooltipOnFieldClick,
    smallTextField,
    isTooltipHidden,
    isSelectLabel,
    disabled,
    patch,
    value,
    trackValidationErrors
  } = props

  return (
    <ConditionalWrapper dependson={dependson}>
      <FieldDataCollector path={path} labelPath={labelPath}>
        <SelectInput
          path={path}
          customLabel={customLabel}
          dependson={dependson}
          choices={choices}
          clue={clue}
          onTooltip={onTooltip}
          onChange={onChange}
          defaultValue={defaultValue}
          hasMostSelected={hasMostSelected}
          useSimpleSelect={useSimpleSelect}
          handleLoanSelectorChanges={handleLoanSelectorChanges}
          outerLabel={outerLabel}
          showTooltipOnFieldClick={showTooltipOnFieldClick}
          smallTextField={smallTextField}
          isTooltipHidden={isTooltipHidden}
          isSelectLabel={isSelectLabel}
          disabled={disabled}
          patch={patch}
          value={value}
          trackValidationErrors={trackValidationErrors}
        />
      </FieldDataCollector>
    </ConditionalWrapper>
  )
}

export default memo(PathSelect) as typeof PathSelect
